import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Style/common.css";
import "./Style/header.css";
import "./Style/StudentMathsDoctorReport.css";
import "./Style/trophy_club.css";
import trophy_club_0 from "./Image/trophy_club/trophy_club_0.png";
import Header from "../../Common/Header";
import Section from "../../Common/Section";
import Loading from "../../Common/Loading";
import SubHeader from "../../Common/SubHeader";

interface ReportOption {
  report_no: number;
  completion_date: string | null;
  label: string;
  is_latest: boolean;
}

interface GradeData {
  year: number;
  score_achieved: number;
  class_for_current_grade: string;
}

interface StrandPerformance {
  strand: number;
  year_level: string | number;
}

interface KnowledgeGap {
  gaps_heading: string;
  knowledge_gaps: string;
  year: number;
}

interface ScoreAnalysis {
  current_score: number;
  current_date: string;
  is_latest: boolean;
  previous_score?: number;
  previous_date?: string;
  improvement?: number;
  improvement_direction?: "up" | "down" | "nochange";
}

interface FullReportResponse {
  student: {
    name: string;
    grade_year: number;
    study_year: number;
  };
  report_info: {
    report_no: number;
    completion_date: string;
    is_latest: boolean;
    md_pass_level: number;
  };
  brick_wall: {
    scores: number[];
    grade_year: number;
    data: GradeData[];
  };
  strand_performance: StrandPerformance[];
  knowledge_gaps: KnowledgeGap[];
  score_analysis: ScoreAnalysis;
  meta: {
    schoolcode: string;
    familycode: string;
    reportno: number;
    md_curriculum: string;
  };
}

interface BasicReportResponse {
  student: {
    name: string;
    grade_year: number;
  };
  reports: ReportOption[];
}

const StudentEnglishCheckReport: React.FC = () => {
  const navigate = useNavigate();
  const CurrentAvatar = sessionStorage.getItem("CurrentAvatar");

  const [availableReports, setAvailableReports] = useState<ReportOption[]>([]);
  const [selectedReportValue, setSelectedReportValue] = useState<string>("");
  const [currentReportNo, setCurrentReportNo] = useState<number>(1);
  const [latestReportNo, setLatestReportNo] = useState<number>(1);
  const [passLevelVisible, setPassLevelVisible] = useState<boolean>(false);
  const [scoreAnalysisVisible, setScoreAnalysisVisible] = useState<boolean>(false);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [myPoints, setMyPoints] = useState(0);
  const [overallWeeklyProgress, setOverallWeeklyProgress] = useState(0);
  const [weekCount, setWeekCount] = useState(0);
  const [trophyData, setTrophyData] = useState<string[]>([]);
  const [userName, setUserName] = useState<string>("");
  const [imageSrc, setImageSrc] = useState("");
  const [grades, setGrades] = useState<GradeData[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);
  const [strandData, setStrandData] = useState<StrandPerformance[]>([]);
  const [knowledgeGaps, setKnowledgeGaps] = useState<KnowledgeGap[]>([]);
  const [loadingApi, setLoadingApi] = useState(true);
  const [passLevel, setPassLevel] = useState(80);
  const [scoreAnalysis, setScoreAnalysis] = useState<ScoreAnalysis | null>(null);
  const [familyCode, setFamilyCode] = useState<string>("");
  const [isLatestReport, setIsLatestReport] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showBackToBottom, setShowBackToBottom] = useState(true);
  const [isResultsByGradeTooltipVisible, setIsResultsByGradeTooltipVisible] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

  let schoolCode = sessionStorage.getItem("SchoolCode");
  let familyCodeSession = sessionStorage.getItem("FamilyCode");
  let login = sessionStorage.getItem("Login");
  let subject = sessionStorage.getItem("Subject");

  const getDateOnly = (dateString?: string | null) => {
    if (!dateString) return "";
    return dateString.split("T")[0];
  };

  const uniqueReportsMap = new Map();

  availableReports.forEach((report) => {
    if (!report?.completion_date) return;
    const dateOnly = report.completion_date.split("T")[0];
    if (!uniqueReportsMap.has(dateOnly)) {
      uniqueReportsMap.set(dateOnly, report);
    }
  });

  const uniqueReports = Array.from(uniqueReportsMap.values());

  useEffect(() => {
    if (familyCodeSession) {
      setFamilyCode(familyCodeSession);
      if (familyCodeSession === "A") {
        setPassLevelVisible(true);
        setScoreAnalysisVisible(true);
      }
    }
  }, [familyCodeSession]);

  const findScrollContainer = (): HTMLElement => {
    const candidates: (HTMLElement | null)[] = [
      (document.scrollingElement as HTMLElement) || null,
      document.getElementById("contents_math"),
      document.getElementById("main_top_container"),
      document.getElementById("contents_english"),
      document.getElementById("contents_physics"),
      document.getElementById("contents_chemistry"),
      document.getElementById("contents_biology"),
      document.body,
      document.documentElement,
    ];

    for (const el of candidates) {
      if (!el) continue;
      try {
        const style = window.getComputedStyle(el);
        const overflowY = style.overflowY;
        if (
          el.scrollHeight > el.clientHeight &&
          (overflowY === "auto" ||
            overflowY === "scroll" ||
            overflowY === "overlay" ||
            overflowY === "visible")
        ) {
          return el;
        }
      } catch (e) { }
    }
    return (document.scrollingElement as HTMLElement) || document.documentElement;
  };

  scrollContainerRef.current = findScrollContainer();
  const registeredContainer = scrollContainerRef.current;

  const handleScroll = () => {
    const el =
      (scrollContainerRef.current ||
        registeredContainer ||
        (document.scrollingElement as HTMLElement) ||
        document.documentElement) as HTMLElement;
    const scrollTop = el.scrollTop;
    const innerH = el.clientHeight || window.innerHeight;
    const scrollHeight = el.scrollHeight;

    const scrollable = Math.max(0, scrollHeight - innerH);
    const TOL = 6;
    const atTop = scrollTop <= TOL;
    const atBottom = scrollTop >= Math.max(0, scrollable - TOL);
    const ratio = scrollable <= 0 ? 0 : scrollTop / scrollable;

    if (atTop) {
      setShowBackToBottom(true);
      setShowBackToTop(false);
      return;
    }

    if (atBottom) {
      setShowBackToTop(true);
      setShowBackToBottom(false);
      return;
    }

    if (ratio < 0.5) {
      setShowBackToBottom(true);
      setShowBackToTop(false);
    } else {
      setShowBackToTop(true);
      setShowBackToBottom(false);
    }
  };

  try {
    if (registeredContainer) {
      registeredContainer.addEventListener("scroll", handleScroll, { passive: true });
    } else {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }
  } catch (e) {
    window.addEventListener("scroll", handleScroll, { passive: true });
  }

  setTimeout(handleScroll, 0);

  useEffect(() => {
    const fetchAvailableReports = async () => {
      try {
        setLoadingReports(true);

        const response = await fetch(`${apiUrl}ec_brickwall/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            SchoolCode: schoolCode,
            FamilyCode: familyCodeSession,
            Login: login,
          }),
        });

        const data: BasicReportResponse = await response.json();

        if (data.reports?.length > 0) {
          const latestReport = data.reports[0];
          setAvailableReports(data.reports);
          setCurrentReportNo(latestReport.report_no);
          setLatestReportNo(latestReport.report_no);
          setUserName(data.student.name);
          setSelectedReportValue(`${latestReport.report_no},${getDateOnly(latestReport.completion_date)}`);
        }

        setLoadingReports(false);
      } catch (err) {
        console.error("Error fetching available reports:", err);
        setLoadingReports(false);
      }
    };

    if (schoolCode && familyCodeSession && login) {
      fetchAvailableReports();
    }
  }, [schoolCode, familyCodeSession, login, apiUrl]);


  useEffect(() => {
    if (currentReportNo > 0 && !loadingReports) {
      fetchFullReportData(currentReportNo);
    }
  }, [currentReportNo, loadingReports]);

  const isPageLoading = loadingReports || loadingGrades || loadingApi;


  const fetchFullReportData = async (reportNo: number) => {
    try {
      setLoadingGrades(true);
      setLoadingApi(true);


      // Call knowledge_gap API (which returns complete data including brick_wall)
      const knowledgeGapResponse = await fetch(`${apiUrl}ec_knowledge_gap/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          SchoolCode: schoolCode,
          FamilyCode: familyCodeSession,
          Login: login,
          ReportNo: reportNo,
        }),
      });

      if (!knowledgeGapResponse.ok) {
        throw new Error("Failed to fetch full report data");
      }

      const knowledgeGapData: FullReportResponse = await knowledgeGapResponse.json();


      setGrades(
        knowledgeGapData.brick_wall.data.map((item) => ({
          year: item.year,
          score_achieved: item.score_achieved,
          class_for_current_grade: item.class_for_current_grade || "",
        }))
      );

      setStrandData(knowledgeGapData.strand_performance);
      setKnowledgeGaps(knowledgeGapData.knowledge_gaps);
      setPassLevel(knowledgeGapData.report_info.md_pass_level);
      setScoreAnalysis(knowledgeGapData.score_analysis);
      setIsLatestReport(knowledgeGapData.report_info.is_latest);
      setLatestReportNo(knowledgeGapData.report_info.report_no);


      setLoadingGrades(false);
      setLoadingApi(false);
    } catch (err) {
      console.error("Error fetching full report:", err);
      setLoadingGrades(false);
      setLoadingApi(false);
    }
  };

  const handleReportChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedReportValue(value);

    if (value && value !== "-1") {
      const reportNo = Number(value.split(",")[0]);
      setCurrentReportNo(reportNo);

      const selectedReport = availableReports.find((r) => r.report_no === reportNo);
      setIsLatestReport(!!selectedReport?.is_latest);
    }
  };

  useEffect(() => {
    if (!loadingGrades && grades.length > 0) {
      const timer = setTimeout(() => {
        const wallDiv = document.getElementById("divBrickwall");
        const wallContainer = document.getElementById("wall_container");

        if (wallDiv && wallContainer) {
          wallDiv.innerHTML = "";
          wallDiv.style.display = "block";
          wallContainer.style.display = "none";
          void wallContainer.offsetHeight;
          wallContainer.style.display = "block";

          const ensureInput = (id: string) => {
            let el = document.getElementById(id) as HTMLInputElement | null;
            if (!el) {
              el = document.createElement("input");
              el.type = "hidden";
              el.id = id;
              document.body.appendChild(el);
            }
            return el;
          };

          const hidScores = ensureInput("hidScoreForBrickwall");
          const hidGrades = ensureInput("hidGrades");
          const hidDates = ensureInput("hidReportDates");
          const hidProgress = ensureInput("hidOverAllWeeklyProgress");

          hidScores.value = grades.map((g) => g.score_achieved).join(",");
          hidGrades.value = grades.map((g) => g.year).join(",");
          hidDates.value = grades.map((g) => "").join(",");
          hidProgress.value = overallWeeklyProgress.toString();

          const existingScript = document.querySelector(
            "script[src='/Script/StudentMathsDoctorReport.js']"
          );
          if (existingScript) {
            existingScript.remove();
          }

          const scriptElement = document.createElement("script");
          scriptElement.src = `/Script/StudentMathsDoctorReport.js?t=${Date.now()}`;
          scriptElement.onload = () => {
            if ((window as any).wall) {
              (window as any).wall("#divBrickwall", (err: any) => {
                if (err) {
                  console.error("Wall failed:", err);
                } else {
                  setTimeout(() => { }, 100);
                }
              });
            } else {
              console.warn("Wall function not available yet");
            }
          };
          scriptElement.onerror = () => {
            console.error("Failed to load Brickwall script");
          };
          document.body.appendChild(scriptElement);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loadingGrades, grades, currentReportNo]);

  useEffect(() => {
    const UserName = sessionStorage.getItem("FName");
    const UserAvatar = sessionStorage.getItem("CurrentAvatar");

    if (UserName) setUserName(UserName);
    if (UserAvatar) setImageSrc(`${UserAvatar}`);

    setMyPoints(1200);
    setWeekCount(10);
    setTrophyData(["Gold", "Silver", "Bronze"]);

    document.body.classList.add("StudyRoom-background");

    const enforceScroll = () => {
      document.body.style.overflowY = "auto";
      document.documentElement.style.overflowY = "auto";
      document.body.style.height = "auto";
      document.documentElement.style.height = "auto";
    };

    enforceScroll();
    const scrollObserver = new MutationObserver(enforceScroll);
    scrollObserver.observe(document.body, { attributes: true });

    const handleClickOutside = () => {
      setIsLegendVisible(false);
    };

    if (isLegendVisible) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.body.classList.remove("StudyRoom-background");
      scrollObserver.disconnect();
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isLegendVisible]);

  const handleTrophyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLegendVisible((prev) => !prev);
  };


  return (
    <>
      {isPageLoading && <Loading />}
      <form>

        <div
          className="back-to-bottom"
          id="back_to_bottom_link"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: "smooth",
            });
          }}
        >
          Back to Top
        </div>

        <div id="main_top_container" style={{ backgroundColor: "white" }}>
          <Header />
          <a
            href="#bottom"
            className="back-to-bottom"
            onClick={(e) => {
              e.preventDefault();
              const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
              try {
                container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
              } catch {
                window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
              }
              setTimeout(() => {
                (container as any).scrollTop = (container as any).scrollHeight;
                (document.body as any).scrollTop = document.body.scrollHeight;
              }, 400);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                try {
                  container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                } catch {
                  window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
                }
                setTimeout(() => {
                  (container as any).scrollTop = (container as any).scrollHeight;
                  (document.body as any).scrollTop = document.body.scrollHeight;
                }, 400);
              }
            }}
            aria-label="Back to bottom"
            role="button"
            tabIndex={0}
            style={{
              opacity: showBackToBottom ? 1 : 0,
              transform: showBackToBottom ? 'translateY(0)' : 'translateY(8px)',
              display: 'block',
              cursor: 'pointer',
              zIndex: 2147483647,
              pointerEvents: showBackToBottom ? 'auto' : 'none',
              position: 'fixed',
              left: 20,
              bottom: 20,
              transition: 'opacity 240ms ease, transform 240ms ease',


              boxShadow: '0 6px 14px rgba(0,0,0,0.18)'
            }}
          >
            Back to Bottom
          </a>

          <a
            href="#top"
            className="back-to-top"
            onClick={(e) => {
              e.preventDefault();
              const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
              try {
                container.scrollTo({ top: 0, behavior: 'smooth' });
              } catch {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
              setTimeout(() => {
                (container as any).scrollTop = 0;
                (document.body as any).scrollTop = 0;
              }, 300);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const container = scrollContainerRef.current || (document.scrollingElement as HTMLElement) || document.documentElement;
                try {
                  container.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setTimeout(() => {
                  (container as any).scrollTop = 0;
                  (document.body as any).scrollTop = 0;
                }, 300);
              }
            }}
            aria-label="Back to top"
            role="button"
            tabIndex={0}
            style={{
              opacity: showBackToTop ? 1 : 0,
              transform: showBackToTop ? 'translateY(0)' : 'translateY(8px)',
              display: 'block',
              cursor: 'pointer',
              zIndex: 2147483647,
              pointerEvents: showBackToTop ? 'auto' : 'none',
              position: 'fixed',
              right: 20,
              bottom: 20,
              transition: 'opacity 240ms ease, transform 240ms ease',
              boxShadow: '0 6px 14px rgba(0,0,0,0.18)'
            }}
          >
            Back to Top
          </a>
          <Section />

          <SubHeader isMSRPage={true} pageType="mathsdoctor">

            <div className="section-container" id="divStudentMDSection">
              <div
                className="content-container"
                style={{ backgroundColor: "white" }}
              >
                <div className="content-top responsive-divider">
                  <div className="report-heading-container">
                    <div
                      id="linkbtnReportLeft"
                      className="report-arrow-left"
                      onClick={() => navigate("/STUDENT/MSR/UserDiary")}
                      style={{ cursor: 'pointer' }}
                    ></div>
                    <div className="report-heading">
                      <span id="spnMathOrMathsReportHeader" style={{ fontSize: "21px", fontFamily: "'Open Sans', sans-serif", fontWeight: "bold" }}>
                       English Check Report
                      </span>
                    </div>
                    <div
                      id="linkbtnReportRight"
                      className="report-arrow-right"
                      onClick={() => navigate("/STUDENT/MSR/StudentActivityReport")}
                      style={{ cursor: 'pointer' }}
                    ></div>
                  </div>

                  <div className="report-displayed-select">
                    <div className="report-displayed-heading">
                      Report Displayed:
                    </div>

                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: "100%",
                      }}
                    >
                      <select
                        id="ddlReports"
                        value={selectedReportValue}
                        onChange={handleReportChange}
                        name="refer"
                        disabled={loadingReports}
                        className="report-displayed"
                        style={{
                          appearance: "none",
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          width: "100%",
                          marginBottom: "0px",
                          padding: "7px 20px",
                          border: "1px solid black",
                          borderRadius: "3px",
                        }}
                      >
                        {loadingReports ? (
                          <option value="">Loading reports...</option>
                        ) : availableReports.length > 0 ? (
                          availableReports.map((report) => {
                            const dateOnly = getDateOnly(report.completion_date);

                            return (
                              <option
                                key={`${report.report_no}-${dateOnly}`}
                                value={`${report.report_no},${dateOnly}`}
                              >
                                {report.label}
                              </option>
                            );
                          })
                        ) : (
                          <option value="-1">-- No Reports --</option>
                        )}

                      </select>

                      <span
                        style={{
                          position: "absolute",
                          right: "7px",
                          top: "50%",
                          transform: "translateY(-42%)",
                          pointerEvents: "none",
                          fontSize: "0.8rem",
                          color: "#333",
                        }}
                      >
                        ▼
                      </span>
                    </div>
                  </div>

                  {passLevelVisible && (
                    <div id="divPassLevel" className="pass-level-container">
                      <div className="pass-level-heading">
                        <div className="help-icon help-click" id="set-pass-click">
                          <div className="tooltip">
                            <div className="expand-close"></div>
                            Changes the set pass level to adjust the following
                            charts:
                            <ul>
                              <li>Grade Level Achieved</li>
                              <li>Area of Study Achieved</li>
                            </ul>
                            <strong>Note:</strong> Only the school administrator has
                            the rights to change this level for the entire school.
                          </div>
                        </div>
                        Set Pass Level:
                      </div>
                      <div className="pass-level-input">
                        <input
                          type="text"
                          id="txtMDPassLevel"
                          className="pass-level-text"
                          value={passLevel}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="left" style={{ backgroundColor: "white" }}>
                  <div className="widget-container" id="divBrickwallHeader">
                    <div className="widget-wrapper">
                      <div className="heading">
                        <div
                          className="help-icon help-click"
                          id="brick-wall-click"
                          onClick={() => setIsResultsByGradeTooltipVisible(!isResultsByGradeTooltipVisible)}
                          style={{
                            display: "inline-block",
                            position: "relative",
                            cursor: "pointer",
                            zIndex: 99999,
                            pointerEvents: "auto",
                            overflow: "visible"
                          }}
                        >
                          <div
                            className="tooltip"
                            style={{
                              position: "absolute",
                              top: "35px",
                              left: "0",
                              width: window.innerWidth < 768 ? "280px" : "450px",
                              minHeight: "150px",
                              background: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: "6px",
                              padding: "12px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                              zIndex: 999999,
                              display: isResultsByGradeTooltipVisible ? "block" : "none",
                              visibility: isResultsByGradeTooltipVisible ? "visible" : "hidden",
                              opacity: isResultsByGradeTooltipVisible ? 1 : 0,
                              pointerEvents: "auto",
                              whiteSpace: "normal"
                            }}
                          >
                            <div className="expand-close"></div>

                            <p>
                              This report shows the students understanding of their current Grade
                              level, up to 3 grades prior (if applicable) and the 2 grades above.
                            </p>

                            <br />

                            <p>
                              The orange bricks provide a visual representation of what has been
                              achieved for each grade level and the red bricks show the knowledge
                              gaps.
                            </p>
                          </div>
                        </div>

                        Results by Grade
                      </div>

                      <div id="wall_container" style={{ margin: 0 }}>

                        <div className="grades" style={{ margin: 0 }}>
                          {loadingGrades ? (
                            <div className="brick-row">
                              <div
                                className="grade-box"
                                style={{ lineHeight: "0px" }}
                              >
                                <p style={{ fontSize: "14px" }}>
                                  Loading grades...
                                </p>
                              </div>
                            </div>

                          ) : grades.length > 0 ? (
                            grades.map((grade, index) => {
                              const currentStudentGrade = sessionStorage.getItem("Grade");
                              const isCurrentGrade = grade.year === Number(currentStudentGrade);

                              return (
                                <div className="brick-row" key={index}>
                                  <div id="whitetext2"></div>
                                  <div className="grade-box">
                                    <p
                                      className={grade.class_for_current_grade}
                                      style={
                                        isCurrentGrade
                                          ? {
                                            backgroundColor: "#dceaf8",
                                            borderRadius: "3px",
                                            padding: "-4px 4px",
                                            color: "#2579d3",
                                          }
                                          : {}
                                      }
                                    >
                                      Grade{" "}
                                      <span className="grade2">{grade.year}</span> (
                                      <span className="percent2">
                                        {Number(grade.score_achieved).toFixed(1)}
                                      </span>
                                      %)
                                    </p>
                                  </div>
                                  <div className="grade-box2">
                                    <p
                                      className={grade.class_for_current_grade}
                                      style={
                                        isCurrentGrade
                                          ? {
                                            backgroundColor: "#dceaf8",
                                            borderRadius: "3px",
                                            padding: "-4px 4px",
                                            color: "#2579d3",
                                          }
                                          : {}
                                      }
                                    >
                                      G<span className="grade2">{grade.year}</span> (
                                      <span className="percent2">
                                        {Number(grade.score_achieved).toFixed(1)}
                                      </span>
                                      %)
                                    </p>
                                  </div>
                                </div>

                              );
                            })
                          ) : (
                            <div className="brick-row">
                              <div className="grade-box">
                                <p>No data available</p>
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <div
                            id="divBrickwall"
                            style={{
                              display:
                                availableReports.length === 0 ? "none" : "block",
                            }}
                          />
                          <input type="hidden" id="hidScoreForBrickwall" />
                          <input
                            type="hidden"
                            id="hidOverAllWeeklyProgress"
                            value={String(overallWeeklyProgress)}
                          />
                          <input
                            type="hidden"
                            id="hidFamilyCode"
                            value={familyCodeSession || ""}
                          />
                          <input
                            type="hidden"
                            id="hidSchoolCode"
                            value={schoolCode || ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="widget-container">
                    <div className="widget-wrapper">
                      <div className="heading">
                        <div
                          className="help-icon help-click"
                          id="grade-achieved-click"
                        >
                          <div className="tooltip" style={{ backgroundColor: "white", width: "400px", height: "200px" }}>
                            <div className="expand-close"></div>
                            <p>
                              The blue boxes show the grade level achieved for each
                              of the areas of study assessed.
                            </p>
                            <p>
                              The red box shows the student's overall grade
                              achievement (based on the pass level set).
                            </p>
                          </div>
                        </div>
                        Grade Achievement by Area of Study and Overall
                      </div>

                      <div className="graph-container">
                        <div className="y-achieve-container">
                          <div className="y-rows-rotate">
                            <div className="y-label-blocks-rotate">
                              Achievement
                              <br></br>
                              Grades
                            </div>
                          </div>
                        </div>

                        <div className="graph">
                          <div className="graph-rows">
                            {[1, 2, 3, 4, 5].map((strandNum) => {
                              const strandResult = strandData.find(
                                (s) => s.strand === strandNum
                              );
                              return (
                                <div className="graph-blocks r6" key={strandNum}>
                                  <div
                                    className="graph-block-text"
                                    id={`divAchievedArea_${strandNum}`}
                                  >
                                    {strandResult?.year_level || "0"}
                                  </div>
                                </div>
                              );
                            })}

                            <div className="graph-blocks overall-block">
                              <div
                                className="graph-block-text"
                                id="divAchievedArea_6"
                              >
                                {strandData.find((s) => s.strand === 6)
                                  ?.year_level || "0"}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="x-label-rows">
                          {[
                            "Grammar",
                            "Punctuation",
                            "Spelling",
                            "Language",
                            "Text Structure",
                            "Overall",
                          ].map((label, idx) => (
                            <div
                              className={`x-label-blocks ${idx === 0 ? "x-label-first" : ""
                                } ${idx === 5 ? "x-label-last" : ""}`}
                              key={idx}
                            >
                              <div className="x-label-text" style={{ fontFamily: "system-ui" }}>{label}</div>
                            </div>
                          ))}
                        </div>
                        <div className="x-label-rows">
                          <div className="x-label-blocks studyarea-container">
                            <div className="x-label-text" style={{ fontFamily: "system-ui" }}>Areas of Study</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {scoreAnalysisVisible && (
                    <div id="divScoreAnalysis" className="widget-container">
                      <div className="widget-wrapper">
                        <div className="heading">
                          <div
                            className="help-icon help-click"
                            id="score-analysis-click"
                          >
                            <div className="tooltip">
                              <div className="expand-close"></div>
                              <p>
                                We have developed our own scoring system to help
                                differentiate between student's results and improve
                                the way we monitor progress.
                              </p>
                              <p>
                                This report shows the score obtained by the student
                                and how they compare to other students in your
                                School in the same Grade.
                              </p>
                              <p>
                                If the student has previously completed Maths Doctor
                                it will also display how much they improved (or not)
                                since the previous assessment.
                              </p>
                            </div>
                          </div>
                          Score Analysis{" "}
                          <span
                            className="not-available"
                            id="spanScoreAnalysisHeading"
                            style={{
                              display: !isLatestReport ? "inline-block" : "none",
                            }}
                          >
                            (Not Available for Old Reports)
                          </span>
                        </div>

                        {isLatestReport && scoreAnalysis && (
                          <div className="analysis-legend" id="divAnalisysLegend">
                            <div className="score-container">
                              <div className="latest-score-icon legend-icon"></div>
                              <div className="score-display-style">
                                <div className="score-text-bold">
                                  (
                                  <span id="spanDateLatest">
                                    {scoreAnalysis.current_date}
                                  </span>
                                  ) Score:
                                </div>
                                <span id="spanLatestScore">
                                  {scoreAnalysis.current_score}
                                </span>
                              </div>
                            </div>
                            <div className="score-container">
                              <div className="previous-score-icon legend-icon"></div>
                              <div className="score-display-style">
                                <div className="score-text-bold">
                                  (
                                  <span id="spanDatePrevious">
                                    {scoreAnalysis.previous_date || ""}
                                  </span>
                                  ) Previous Score:
                                </div>
                                <span id="spanPreviousScore">
                                  {scoreAnalysis.previous_score || "-"}
                                </span>
                              </div>
                            </div>
                            <div className="score-container">
                              <div className="score-display-style improvement-container">
                                |
                              </div>
                              <div className="score-display-style">
                                <div className="score-display-style score-text-bold">
                                  Improvement:
                                </div>
                                <div
                                  className={`score-display-style ${scoreAnalysis.improvement_direction
                                      ? `score-${scoreAnalysis.improvement_direction}`
                                      : ""
                                    }`}
                                  id="divImprovementIndicator"
                                ></div>
                                <div
                                  className="score-display-style"
                                  id="divTotalStudents"
                                >
                                  -
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {isLatestReport && (
                          <div
                            className="analysis-graph-container"
                            id="divAnalisysGraph"
                          >
                            <div className="analysis-graph">
                              <div className="analysis-graph-bg">
                                <div
                                  className="icon-wrapper"
                                  id="divScoreAnalysisIndicators"
                                >
                                  <div
                                    className="latest-score-icon graph-icon"
                                    id="divLatestScorePosition"
                                  ></div>
                                  <div
                                    className="previous-score-icon graph-icon"
                                    id="divPreviousScorePosition"
                                  ></div>
                                  <div id="divDottedLineGradeAverage">
                                    <div className="school-dotted-line"></div>
                                    <div className="dotted-text">Grade Avg</div>
                                  </div>
                                  <div id="divDottedLineGradeTop">
                                    <div className="dotted-text-top">Grade Top</div>
                                    <div className="school-dotted-line"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="analysis-label-rows">
                              {[
                                "0",
                                "100",
                                "200",
                                "300",
                                "400",
                                "500",
                                "600",
                                "700",
                                "800",
                                "900",
                                "1000",
                                "1100",
                              ].map((label, idx) => (
                                <div className="analysis-label-blocks" key={idx}>
                                  <div className="analysis-label-text">{label}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="right" style={{ backgroundColor: "#fff" }}>
                  <div className="widget-container">
                    <div className="widget-wrapper">
                      <div className="heading">
                        <div
                          className="help-icon help-click"
                          id="knowledge-gaps-click"
                        >
                          <div className="tooltip" style={{ background: "white" }}>
                            <div className="expand-close"></div>
                            Lists all the topics identifed for revison by Grade
                            Level.
                          </div>
                        </div>
                        Knowledge Gaps Identified
                      </div>
                      <div
                        className="knowledge-container"
                        id="divKnowledgeGapContainer"
                      >
                        <div className="gap-row-1">
                          {loadingApi ? (
                            <div className="gaps-container">
                              <div className="gaps-purple">
                                <p className="gap-1-grade">Loading...</p>
                              </div>
                              <span className="gaps-text" style={{ fontFamily: "'Open Sans', sans-serif" }}>
                                Loading knowledge gaps...
                              </span>
                            </div>
                          ) : grades.length > 0 ? (
                            grades.map((grade, index) => {
                              const gapForGrade = knowledgeGaps.find(
                                (gap) => gap.year === grade.year
                              );
                              return (
                                <div
                                  className="gaps-container"
                                  id={`gap-${index + 1}-container`}
                                  key={index}
                                >
                                  <div className="gaps-purple" style={{ fontFamily: "system-ui" }}>
                                    <p className="gap-1-grade" style={{ fontFamily: "system-ui", textShadow: " 0.5px rgba(255, 255, 255, 0.7)" }}>
                                      {gapForGrade?.gaps_heading || `Grade ${grade.year} Gaps`}
                                    </p>
                                  </div>
                                  <span
                                    className="gaps-text"
                                    style={{ lineHeight: "0", fontFamily: "system-ui" }}
                                  >
                                    {gapForGrade?.knowledge_gaps || "No gaps found"}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="gaps-container">
                              <div className="gaps-purple">
                                <p className="gap-1-grade">No Data</p>
                              </div>
                              <span className="gaps-text">
                                No grade data available.
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SubHeader>

          <input type="hidden" id="hidScoreForBrickwall" />
          <input
            type="hidden"
            id="hidOverAllWeeklyProgress"
            value={String(overallWeeklyProgress)}
          />
          <input type="hidden" id="hidSchoolCode" value={schoolCode || ""} />
          <input
            type="hidden"
            id="hidFamilyCode"
            value={familyCodeSession || ""}
          />
        </div>
      </form>
    </>
  );
};

export default StudentEnglishCheckReport;